import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Share2, DollarSign, Users, Clock, Gift } from "lucide-react";
import { useAuthStore } from "../store/authStore";

const API_URL = import.meta.env.VITE_API_URL || "http://64.23.240.10/api/v1";
const BASE_API_URL = API_URL.replace("/v1", "");

interface ReferralStats {
  has_code: boolean;
  code: string;
  referral_url: string;
  total_referred: number;
  total_earned_cents: number;
  total_paid_cents: number;
  pending_cents: number;
  commission_percent: number;
}

export default function ReferralPage() {
  const { token } = useAuthStore();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function loadStats() {
      try {
        const res = await fetch(`${BASE_API_URL}/referral/my-code`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const codeData = await res.json();

        const statsRes = await fetch(`${BASE_API_URL}/referral/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const statsData = await statsRes.json();
        setStats({ ...codeData, ...statsData });
      } catch {
        setStats(null);
      } finally {
        setLoading(false);
      }
    }
    loadStats();
  }, [token]);

  const copyLink = () => {
    if (stats?.referral_url) {
      navigator.clipboard.writeText(stats.referral_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareText = `I have been using ResidualVault to optimize my crypto staking yields — they also have a complete education guide that covers APY analysis, protocol risk, and portfolio construction. Worth reading: ${stats?.referral_url || ""}`;

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-dark">
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-sky-blue/10 border border-sky-blue/20 mx-auto mb-6">
            <Gift size={32} className="text-sky-blue" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">
            Earn 10% on Every Manual You Refer
          </h1>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Share your unique link. When someone buys any ResidualVault education
            manual through your link, you earn 10% of the sale. Automatically.
          </p>
        </div>

        {/* Referral Link */}
        <div className="bg-navy-dark border border-sky-blue/20 rounded-2xl p-8 mb-8">
          <h2 className="text-xl font-bold text-white mb-4">Your Referral Link</h2>
          <div className="flex gap-3">
            <input
              type="text"
              readOnly
              value={stats?.referral_url || ""}
              className="flex-1 bg-steel-blue/10 border border-sky-blue/20 rounded-xl px-4 py-3 text-white text-sm font-mono"
            />
            <button
              onClick={copyLink}
              className="flex items-center gap-2 px-6 py-3 bg-sky-blue hover:bg-sky-blue/90 text-navy-dark font-bold rounded-xl transition whitespace-nowrap"
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
              {copied ? "Copied" : "Copy Link"}
            </button>
          </div>

          <div className="flex gap-3 mt-4">
            <a
              href={twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 bg-steel-blue/20 hover:bg-steel-blue/30 text-white rounded-xl transition text-sm font-medium"
            >
              <Share2 size={16} /> Share on X
            </a>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 bg-steel-blue/20 hover:bg-steel-blue/30 text-white rounded-xl transition text-sm font-medium"
            >
              <Share2 size={16} /> Share on WhatsApp
            </a>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-navy-dark border border-sky-blue/20 rounded-xl p-5 text-center">
            <Users size={20} className="text-sky-blue mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stats?.total_referred || 0}</p>
            <p className="text-xs text-white/50 mt-1">Total Referrals</p>
          </div>
          <div className="bg-navy-dark border border-sky-blue/20 rounded-xl p-5 text-center">
            <DollarSign size={20} className="text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">
              ${((stats?.total_earned_cents || 0) / 100).toFixed(2)}
            </p>
            <p className="text-xs text-white/50 mt-1">Total Earned</p>
          </div>
          <div className="bg-navy-dark border border-sky-blue/20 rounded-xl p-5 text-center">
            <Clock size={20} className="text-yellow-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">
              ${((stats?.pending_cents || 0) / 100).toFixed(2)}
            </p>
            <p className="text-xs text-white/50 mt-1">Pending</p>
          </div>
          <div className="bg-navy-dark border border-sky-blue/20 rounded-xl p-5 text-center">
            <DollarSign size={20} className="text-sky-blue mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">
              ${((stats?.total_paid_cents || 0) / 100).toFixed(2)}
            </p>
            <p className="text-xs text-white/50 mt-1">Paid Out</p>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-navy-dark border border-sky-blue/20 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-white mb-6">How It Works</h2>
          <div className="space-y-5">
            {[
              "Copy your unique referral link above",
              "Share it anywhere — social media, email, Discord, forums",
              "When someone purchases any ResidualVault manual through your link, you earn 10% automatically",
              "Commissions are tracked in real time in your dashboard",
              "Payouts processed monthly via PayPal or bank transfer (minimum $20 payout threshold)",
            ].map((step, i) => (
              <div key={i} className="flex gap-4 items-start">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-sky-blue/20 border border-sky-blue/30 flex items-center justify-center text-sky-blue font-bold text-sm">
                  {i + 1}
                </span>
                <p className="text-white/70 text-sm pt-1">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-8">
          <Link
            to="/store"
            className="text-sky-blue hover:text-sky-blue/80 text-sm font-medium transition"
          >
            View Education Store →
          </Link>
        </div>
      </div>
    </div>
  );
}
